import axios from "axios";
import apiClient from "./apiClient";
import { _post } from "./apiClient";
import { apiUrls } from "../config/apiUrls.const";
import { AccountPayload, ProfilePayload } from "../types";
import { setSecureItem, removeSecureItem } from "../utils/LocalStorageManager";

export const loginUser = async (email: string, password: string) => {
  const { data } = await apiClient.post(apiUrls.login, { email, password });

  const accessToken = data?.data?.token?.access_token;
  const refreshToken = data?.data?.token?.refresh_token; // 💡 Extract refresh token

  if (accessToken) {
    // 💡 Update keys to match your updated apiClient interceptor configuration rules
    setSecureItem("access_token", accessToken);
    if (refreshToken) {
      setSecureItem("refresh_token", refreshToken);
    }

    const userData = await apiClient.get(apiUrls.user);
    const user = userData?.data?.data;

    setSecureItem("user", user);
    return userData; // return minimal user info
  }

  throw new Error(data.message || "Login failed");
};

export const registerUser = async (
  name: string,
  mobile_no: string,
  email: string,
  password: string,
  password_confirmation: string,
  type: string,
  allowed_service: string 
) => {
  try {
    const payload: Record<string, any> = {
      name,
      mobile_no,
      email,
      password,
      password_confirmation,
      type,
      allowed_service
    };

    const { data } = await apiClient.post(apiUrls.register, payload);

    // 💡 Updated parsing path to match your new Laravel controller payload response matrix
    const accessToken = data?.data?.token?.access_token;
    const refreshToken = data?.data?.token?.refresh_token;

    if (accessToken) {
      setSecureItem("access_token", accessToken);
      if (refreshToken) {
        setSecureItem("refresh_token", refreshToken);
      }

      // Fetch user profile
      const userData = await apiClient.get(apiUrls.user);
      setSecureItem("user", userData?.data?.data);

      return { success: true, user: userData?.data?.data };
    }

    throw new Error("Registration failed");
  } catch (error: unknown) {
    if (axios.isAxiosError(error) && error.response) {
      const { status, data } = error.response;

      if (status === 422 && data.errors) {
        return {
          success: false,
          type: "validation",
          errors: data.errors,
        };
      }

      if (status === 400 || status === 401) {
        return {
          success: false,
          type: "auth",
          message: data.message || "Invalid registration attempt.",
        };
      }
    }

    return {
      success: false,
      type: "server",
      message: "Something went wrong. Please try again later.",
    };
  }
};

export const getUser = async () => {
  const { data } = await apiClient.get(apiUrls.user);
  return data;
};

export const getProfile = async () => {
  const { data } = await apiClient.get(apiUrls.profile);
  return data;
};

export const updateProfile = (payload: Partial<ProfilePayload>) => {
  return _post(apiUrls.updateProfile, payload);
};

export const updateAccount = (payload: Partial<AccountPayload>) => {
  return _post(apiUrls.updateAccount, payload);
};

export const updatePhoto = async (formData: FormData) => {
  const { data } = await apiClient.post(apiUrls.updatePhoto, formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
  return data;
};

export const logout = async () => {
  try {
    await apiClient.post(apiUrls.logout); // try to notify backend
  } catch (error) {
    console.warn("Logout request failed, but clearing local data anyway:", error);
  } finally {
    // 💡 Clean wipe out ALL active authentication cache spaces using your encryption wrapper
    removeSecureItem("access_token");
    removeSecureItem("refresh_token");
    removeSecureItem("user");
    removeSecureItem("businesses");
    removeSecureItem("binfo");

    // Redirect to login/home
    window.location.href = "/";
  }
};